<script lang="ts">
import { computed, useSlots } from 'vue';
import { useSharedState } from 'composables/useSharedState';
import { useLayout } from 'components/Layout/useLayout';
import { MenuProps } from './MenuProps';
import { MenuItemProps } from './MenuItemProps';
</script>

<script setup lang="ts" generic="T extends MenuItemProps">
import type { ButtonProps } from 'types';

const props = withDefaults(defineProps<MenuProps<MenuItemProps>>(), {
  logo: true,
  externalIcon: 'open-in-new',
  collapsible: true,
  open: false
});

const emit = defineEmits<(event: 'update:open', value: boolean) => void>();

const state = useSharedState<MenuProps<MenuItemProps>>(props);

const slots = useSlots();

const { toggleCollapsed, collapsed } = useLayout();

const openProxy = computed({
  get: () => state.open?.value ?? false,
  set: (value: boolean) => {
    if (state.open) state.open.value = value;
    emit('update:open', value);
  }
});

const toggleCollapse = () => {
  toggleCollapsed();
};

const toggleOpen = () => {
  openProxy.value = !openProxy.value;
};
</script>

<template>
  <nav
    class="e-menu-root"
    role="navigation"
    data-orientation="vertical"
    :data-collapsed="collapsed"
    :data-open="openProxy"
  >
    <div class="e-menu">
      <div
        class="e-menu-header"
        :data-collapsed="collapsed"
      >
        <slot name="header">
          <e-link-base to="/">
            <e-logo
              v-if="state.logo"
              class="e-menu-logo"
              v-bind="state.logo"
              :icon="collapsed"
            ></e-logo>
          </e-link-base>

          <div class="e-menu-header-options">
            <e-appearance variant="ghost"></e-appearance>
            <e-button
              variant="ghost"
              :icon="openProxy ? 'close' : 'menu'"
              @click="toggleOpen"
            >
            </e-button>
          </div>
        </slot>
      </div>
      <div
        v-if="!!slots.leading"
        class="e-menu-leading"
      >
        <slot name="leading" />
      </div>
      <ul class="e-menu-list">
        <slot>
          <e-menu-item
            v-for="(item, index) in items"
            :key="index"
            v-bind="item"
          />
        </slot>
      </ul>
    </div>

    <div class="e-menu-footer-wrapper">
      <div class="e-menu-footer">
        <slot name="footer">
          <e-appearance variant="ghost"></e-appearance>
        </slot>
      </div>

      <e-button
        v-if="state.collapsible?.value"
        class="e-menu-footer-collapsible-button"
        variant="ghost"
        icon="collapse"
        v-bind="
          typeof state.collapsible.value === 'object' && (state.collapsible.value as ButtonProps)
        "
        @click="toggleCollapse"
      >
      </e-button>
    </div>

    <e-link-base
      v-if="state.collapsible?.value"
      as="button"
      type="button"
      class="e-menu-background-button"
      @click="toggleCollapse"
    ></e-link-base>
  </nav>
</template>
